import type {
  DraftOrderDTO,
  OrderDTO,
  OrderFinancialStatus,
  OrderFulfillmentStatus,
  OrderHeaderDispatchStatus,
  ProductionJobCoreDTO,
  ShopifyCustomer,
  ShopifyLineItem,
  ShopifyMailingAddress,
  MoneyBag,
} from "../../api";

/**
 * The unified row the orders grid shows.
 *
 * Draft orders and completed orders are different tables with different
 * Shopify payloads, but operationally they are one queue — so, exactly as the
 * Vaadin `OrderData` wrapper did, this normalises both into one shape and
 * remembers which side it came from.
 */
export interface OrderRow {
  key: string;
  isDraft: boolean;
  order?: OrderDTO;
  draft?: DraftOrderDTO;
}

export function toRow(entity: OrderDTO | DraftOrderDTO, isDraft: boolean): OrderRow {
  return isDraft
    ? { key: `draft-${entity.id}`, isDraft: true, draft: entity as DraftOrderDTO }
    : { key: `order-${entity.id}`, isDraft: false, order: entity as OrderDTO };
}

const shopify = (row: OrderRow) => (row.isDraft ? row.draft?.draft_order : row.order?.order);

export function headerId(row: OrderRow): number {
  return (row.isDraft ? row.draft?.id : row.order?.id) ?? 0;
}

export function storeId(row: OrderRow): number {
  return (row.isDraft ? row.draft?.store_id : row.order?.store_id) ?? 0;
}

export function clientId(row: OrderRow): number | null {
  return (row.isDraft ? row.draft?.client_id : row.order?.client_id) ?? null;
}

export function orderGid(row: OrderRow): string {
  return shopify(row)?.id ?? "";
}

/** The numeric part of the Shopify GID, which is what the admin URL wants. */
export function orderIdStripped(row: OrderRow): string {
  const gid = orderGid(row);
  return gid.replace("gid://shopify/DraftOrder/", "").replace("gid://shopify/Order/", "");
}

export function orderName(row: OrderRow): string {
  return shopify(row)?.name ?? "";
}

export function orderKind(row: OrderRow): "DRAFT" | "ORDER" {
  return row.isDraft ? "DRAFT" : "ORDER";
}

/** Drafts are never fulfilled and never paid; Shopify only reports on orders. */
export function fulfillmentStatus(row: OrderRow): OrderFulfillmentStatus {
  if (row.isDraft) return "UNFULFILLED";
  return (row.order?.order.displayFulfillmentStatus ?? "UNFULFILLED") as OrderFulfillmentStatus;
}

export function financialStatus(row: OrderRow): OrderFinancialStatus {
  if (row.isDraft) return "PENDING";
  return (row.order?.order.displayFinancialStatus ?? "PENDING") as OrderFinancialStatus;
}

export function orderDate(row: OrderRow): string | null {
  return shopify(row)?.createdAt ?? null;
}

export function customer(row: OrderRow): ShopifyCustomer | null {
  return shopify(row)?.customer ?? null;
}

export function shippingAddress(row: OrderRow): ShopifyMailingAddress | null {
  return shopify(row)?.shippingAddress ?? null;
}

export function lineItems(row: OrderRow): ShopifyLineItem[] {
  return shopify(row)?.lineItems?.nodes ?? [];
}

export function itemCount(row: OrderRow): number {
  return lineItems(row).length;
}

export function total(row: OrderRow): MoneyBag | null {
  return shopify(row)?.totalPriceSet ?? null;
}

/**
 * How much of the order has been sent to production.
 *
 * A draft can't be dispatched at all; a fulfilled order is done; otherwise it
 * is the count of production jobs against the number of line items — one job
 * short of the line count is a partial dispatch.
 */
export function dispatchStatus(
  row: OrderRow,
  jobs: ProductionJobCoreDTO[] | undefined,
): OrderHeaderDispatchStatus {
  if (row.isDraft) return "NOT_DISPATCHABLE";
  if (fulfillmentStatus(row) === "FULFILLED") return "DISPATCHED";
  if (!row.order?.order.fulfillable) return "NOT_DISPATCHABLE";

  const dispatched = jobs?.length ?? 0;
  const expected = itemCount(row);
  if (dispatched === 0) return "NOT_DISPATCHED";
  return dispatched < expected ? "PARTIAL_DISPATCH" : "DISPATCHED";
}
